import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { alpha } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { AdvisorComposer } from "src/advisor/AdvisorComposer";
import { AdvisorConversation } from "src/advisor/AdvisorConversation";
import { useAdvisor } from "src/advisor/AdvisorProvider";
import { TOP_BAR_HEIGHT } from "src/components/AppShell/layout";

/** Figma "AI Advisor column": 380px including its right rule. */
export const ADVISOR_COLUMN_WIDTH = 380;
const ACTION_SIZE = 32;

const HeaderAction: FC<{ label: string; onClick: () => void; children: React.ReactNode }> = ({
	label,
	onClick,
	children
}) => (
	<Tooltip title={label}>
		<IconButton
			onClick={onClick}
			aria-label={label}
			sx={{ width: ACTION_SIZE, height: ACTION_SIZE, borderRadius: "8px", "& svg": { fontSize: 18 } }}>
			{children}
		</IconButton>
	</Tooltip>
);

/**
 * Desktop advisor (Figma D12 / D14b): a full-height column in the left slot under the top bar, in
 * place of the side nav. The chevron collapses it and brings the nav back.
 */
export const AdvisorColumn: FC = () => {
	const { t } = useTranslation();
	const { chat, closeAdvisor } = useAdvisor();

	return (
		<Box
			component="aside"
			aria-label={t("advisor.title")}
			sx={{
				display: "flex",
				flexDirection: "column",
				flexShrink: 0,
				width: ADVISOR_COLUMN_WIDTH,
				position: "sticky",
				top: TOP_BAR_HEIGHT.md,
				height: `calc(100dvh - ${TOP_BAR_HEIGHT.md}px)`,
				bgcolor: "background.paper",
				borderRight: 1,
				borderColor: "divider"
			}}>
			<Stack direction="row" spacing={1} sx={{ alignItems: "center", pl: 2, pr: 1, py: 1.5, flexShrink: 0 }}>
				<AutoAwesomeIcon color="primary" sx={{ fontSize: 20 }} />
				<Typography component="h2" sx={{ fontSize: 17, fontWeight: 600 }}>
					{t("advisor.title")}
				</Typography>
				<Box
					component="span"
					sx={theme => ({
						px: 1,
						py: 0.25,
						borderRadius: "10px",
						bgcolor: alpha(theme.palette.primary.main, 0.12),
						color: "primary.main",
						fontSize: 12,
						fontWeight: 600,
						lineHeight: "15px"
					})}>
					{t("advisor.beta")}
				</Box>
				<Box sx={{ flex: 1 }} />
				<HeaderAction label={t("advisor.newConversation")} onClick={chat.reset}>
					<AddIcon />
				</HeaderAction>
				<HeaderAction label={t("advisor.collapse")} onClick={closeAdvisor}>
					<ChevronLeftIcon />
				</HeaderAction>
			</Stack>
			<AdvisorConversation chat={chat} />
			<AdvisorComposer variant="card" busy={chat.busy} onSend={chat.send} />
		</Box>
	);
};
